// Pasted HTML from Claude, ChatGPT, emails or Word wraps text in layout
// elements (div/section/header), framework comments and data-* attributes.
// TinyMCE's valid_elements would drop the wrappers but glue their lines
// together ("Zeile 1Zeile 2"), and it always keeps data-* attributes.
// This turns text-only wrappers into paragraphs (or line breaks inside links,
// list items and cells) and unwraps the rest first.

const WRAPPERS = ['div', 'section', 'article', 'header', 'footer', 'aside', 'main', 'nav'];
const BLOCKS = WRAPPERS.concat([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
  'table', 'blockquote', 'figure', 'hr', 'pre',
]);
// Only here may a wrapper become a <p>. Inside inline elements (<a>, <strong>)
// a <p> would make TinyMCE drop the link/bold; inside li/td it adds margins.
const PARAGRAPH_CONTEXTS = ['body', 'blockquote'];
// Line boundaries: a <br> right next to one of these is redundant.
const BOUNDARIES = BLOCKS.concat([
  'body', 'td', 'th', 'tr', 'tbody', 'thead', 'tfoot', 'caption', 'figcaption', 'dl', 'dt', 'dd',
]);
const CONTENT_WITHOUT_TEXT = 'img,hr,table';
const NOT_RENDERED = 'style,script,title,template,noscript,meta,link';
const NEEDS_CLEANING = /<!--|<(div|section|article|header|footer|aside|main|nav)[\s>]|\sdata-(?!mce-)/i;

const tagOf = (node: Node | null) => (node && node.nodeType === 1 ? (node as Element).tagName.toLowerCase() : '');

const isBlank = (el: Element) =>
  !el.querySelector(CONTENT_WITHOUT_TEXT) && !(el.textContent || '').replace(/\u00a0/g, ' ').trim();

// Nested wrappers are resolved later, so look past them to the real container.
const contextOf = (el: Element) => {
  let parent = el.parentNode;
  while (parent && WRAPPERS.indexOf(tagOf(parent)) !== -1) {
    parent = parent.parentNode;
  }
  return tagOf(parent);
};

const unwrap = (el: Element) => {
  const parent = el.parentNode!;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
};

// Flattens the tree into what a reader sees in order: content (text, images),
// line breaks and line boundaries (block starts/ends). Empty inline elements
// and whitespace contribute nothing.
interface Token {
  kind: 'content' | 'br' | 'boundary';
  node?: Node;
}
const tokenize = (node: Node, tokens: Token[]) => {
  Array.from(node.childNodes).forEach((child) => {
    const tag = tagOf(child);
    if (child.nodeType === 3) {
      if ((child.textContent || '').replace(/\u00a0/g, ' ').trim()) {
        tokens.push({ kind: 'content' });
      }
    } else if (tag === 'br') {
      tokens.push({ kind: 'br', node: child });
    } else if (tag === 'img') {
      tokens.push({ kind: 'content' });
    } else if (BOUNDARIES.indexOf(tag) !== -1) {
      tokens.push({ kind: 'boundary' });
      tokenize(child, tokens);
      tokens.push({ kind: 'boundary' });
    } else if (child.nodeType === 1) {
      tokenize(child, tokens);
    }
  });
  return tokens;
};

// A wrapper's line break is only needed between two pieces of content; at the
// start or end of a line, or next to another <br>, it would add a blank line.
const dropRedundantBreaks = (body: HTMLElement, breaks: Node[]) => {
  const marked = new Set(breaks);
  const tokens = tokenize(body, []);
  const kept: Token[] = [];
  tokens.forEach((token, i) => {
    if (token.kind === 'br' && marked.has(token.node!)) {
      const prev = kept[kept.length - 1];
      const next = tokens[i + 1];
      if (!prev || prev.kind !== 'content' || !next || next.kind !== 'content') {
        token.node!.parentNode!.removeChild(token.node!);
        return;
      }
    }
    kept.push(token);
  });
};

export default function cleanPastedHtml(html: string): string {
  if (!NEEDS_CLEANING.test(html)) {
    return html;
  }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;

  const comments: Node[] = [];
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_COMMENT);
  while (walker.nextNode()) {
    comments.push(walker.currentNode);
  }
  comments.forEach((c) => c.parentNode!.removeChild(c));

  // Never rendered, and their text would count as content below.
  Array.from(body.querySelectorAll(NOT_RENDERED)).forEach((el) => el.parentNode!.removeChild(el));

  Array.from(body.querySelectorAll('*')).forEach((el) => {
    Array.from(el.attributes)
      .filter((a) => a.name.indexOf('data-') === 0 && a.name.indexOf('data-mce-') !== 0)
      .forEach((a) => el.removeAttribute(a.name));
  });

  // Deepest first, so a wrapper sees its children already resolved.
  const breaks: Node[] = [];
  const addBreak = (before: Node | null, parent: Node) => {
    const br = doc.createElement('br');
    parent.insertBefore(br, before);
    breaks.push(br);
  };
  Array.from(body.querySelectorAll(WRAPPERS.join(','))).reverse().forEach((el) => {
    const parent = el.parentNode!;
    if (PARAGRAPH_CONTEXTS.indexOf(contextOf(el)) === -1) {
      // Keep the wrapper's line without introducing a block element;
      // dropRedundantBreaks removes the breaks that turn out unnecessary.
      addBreak(el, parent);
      addBreak(el.nextSibling, parent);
      unwrap(el);
    } else if (isBlank(el)) {
      parent.removeChild(el);
    } else if (Array.from(el.children).some((c) => BLOCKS.indexOf(tagOf(c)) !== -1)) {
      unwrap(el);
    } else {
      const p = doc.createElement('p');
      while (el.firstChild) {
        p.appendChild(el.firstChild);
      }
      parent.replaceChild(p, el);
    }
  });
  dropRedundantBreaks(body, breaks);

  // Pasted fragments often end (or start) with empty spacer paragraphs.
  const edge = (pick: () => Element | null) => {
    let el = pick();
    while (el && el.tagName === 'P' && isBlank(el) && !el.querySelector('br')) {
      body.removeChild(el);
      el = pick();
    }
  };
  edge(() => body.lastElementChild);
  edge(() => body.firstElementChild);

  return body.innerHTML;
}
